import { Field, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';

import { Credential } from '@/react/portainer/settings/sharedCredentials/types';
import { CustomTemplate } from '@/react/portainer/templates/custom-templates/types';
import { KaasProvider } from '@/react/portainer/environments/types';
import { isVersionSmaller } from '@/react/common/semver-utils';
import { NameField } from '@/react/portainer/environments/common/NameField/NameField';

import { FormControl } from '@@/form-components/FormControl';
import { Input, Select } from '@@/form-components/Input';
import { Loading } from '@@/Widget/Loading';
import { Link } from '@@/Link';
import { TextTip } from '@@/Tip/TextTip';
import { HelpLink } from '@@/HelpLink';

import { useCloudProviderOptions } from '../queries';
import { FormValues, isAPIKaasInfo } from '../types';
import { useSetAvailableOptionFormik } from '../useSetAvailableOptionFormik';
import { CredentialsField } from '../shared/CredentialsField';
import { ActionsSection } from '../shared/ActionsSection';
import { MoreSettingsSection } from '../../shared/MoreSettingsSection';
import { CustomTemplateSelector } from '../../shared/CustomTemplateSelector';
import { KaaSInfoText } from '../shared/KaaSInfoText';
import { providerTitles } from '../../WizardK8sInstall/types';

type Props = {
  credentials: Credential[];
  customTemplates: CustomTemplate[];
  provider: KaasProvider;
  isSubmitting: boolean;
};

// ApiCreateClusterForm handles form changes, conditionally renders inputs, and manually set values
export function ApiCreateClusterForm({
  credentials,
  customTemplates,
  provider,
  isSubmitting,
}: Props) {
  const [isOptionsForce, setIsOptionsForce] = useState(false);
  const { values, setFieldValue, errors } = useFormikContext<FormValues>();
  const {
    region,
    credentialId,
    kubernetesVersion,
    api: { networkId, nodeSize },
  } = values;

  const selectedCredential =
    credentials.find((c) => c.id === credentialId) || credentials[0];

  const cloudOptionsQuery = useCloudProviderOptions(
    provider,
    isAPIKaasInfo,
    selectedCredential,
    isOptionsForce
  );

  const isUnderpoweredNode =
    provider === KaasProvider.CIVO &&
    nodeSize.includes('xsmall') &&
    !isVersionSmaller(kubernetesVersion, '1.27.0');

  const cloudOptions = cloudOptionsQuery.data;

  const filteredNetworkOptions = useMemo(
    () => cloudOptions?.networks?.[region] || [],
    [cloudOptions?.networks, region]
  );

  // if the credentials change, select the first credential available
  useEffect(() => {
    const credential = credentials[0];
    setFieldValue('credentialId', credential.id);
  }, [credentials, setFieldValue]);

  // when the options change, set the available options in the select inputs
  useSetAvailableOptionFormik(
    filteredNetworkOptions,
    networkId,
    'api.networkId'
  );
  useSetAvailableOptionFormik(cloudOptions?.regions, region, 'region');
  const defaultNodeSize =
    provider === KaasProvider.CIVO
      ? cloudOptionsQuery.data?.nodeSizes?.[1].value // the first value is xsmall, so skip it
      : undefined;
  useSetAvailableOptionFormik(
    cloudOptions?.nodeSizes,
    nodeSize,
    'api.nodeSize',
    defaultNodeSize
  );
  useSetAvailableOptionFormik(
    cloudOptions?.kubernetesVersions,
    kubernetesVersion,
    'kubernetesVersion'
  );

  // when the region changes, update the selected network
  useEffect(() => {
    if (filteredNetworkOptions.length > 0 && region) {
      setFieldValue('api.networkId', filteredNetworkOptions[0].value);
    }
  }, [region, filteredNetworkOptions, setFieldValue]);

  return (
    <>
      <KaaSInfoText />
      <NameField
        tooltip="Name of the cluster and environment"
        placeholder="e.g. my-cluster-name"
      />
      <CredentialsField credentials={credentials} />
      {cloudOptionsQuery.isError && (
        <TextTip color="orange">
          Error getting {providerTitles[provider]} info. Go to&nbsp;
          <Link
            to="portainer.settings.sharedcredentials.credential"
            params={{ id: credentialId }}
            title="shared credential settings"
            data-cy="shared-credential-settings-link"
          >
            shared credential settings
          </Link>
          &nbsp;to ensure the API key is valid.
        </TextTip>
      )}
      {(cloudOptionsQuery.isLoading || cloudOptionsQuery.isFetching) && (
        <Loading />
      )}
      {/* cluster details inputs */}
      {cloudOptions && (
        <>
          <FormControl
            label="Region"
            tooltip="Region in which to provision the cluster"
            inputId="kaas-region"
            errors={errors.region}
          >
            <Field
              name="region"
              as={Select}
              id="kaa-region"
              data-cy="kaasCreateForm-regionSelect"
              options={cloudOptions.regions}
            />
          </FormControl>
          <FormControl
            label="Node size"
            tooltip="Size of each node deployed in the cluster"
            inputId="kaas-nodeSize"
            errors={errors.api?.nodeSize}
          >
            <Field
              name="api.nodeSize"
              as={Select}
              id="kaas-nodeSize"
              data-cy="kaasCreateForm-nodeSizeSelect"
              options={cloudOptions.nodeSizes}
            />
            {isUnderpoweredNode && (
              <TextTip color="orange" className="mt-2">
                Civo clusters using version 1.27 Kubernetes or above on a node
                size of Extra Small may fail to complete provisioning as the
                compute resources are too limited for required workloads. See{' '}
                <HelpLink docLink="/admin/environments/add/kaas/civo#a-note-about-civo-versions">
                  Portainer documentation
                </HelpLink>{' '}
                for version-specific issues.
              </TextTip>
            )}
          </FormControl>
          <FormControl
            label="Node count"
            tooltip="Number of nodes to provision in the cluster"
            inputId="kaas-nodeCount"
            errors={errors.nodeCount}
          >
            <Field
              name="nodeCount"
              as={Input}
              type="number"
              data-cy="kaasCreateForm-nodeCountInput"
              min={1}
              max={1000}
              id="kaas-nodeCount"
              placeholder="3"
            />
          </FormControl>
          {region && filteredNetworkOptions.length > 0 && (
            <FormControl
              label="Network ID"
              tooltip="ID of network attached to the cluster"
              inputId="kaas-networkId"
              errors={errors.api?.networkId}
            >
              <Field
                name="api.networkId"
                as={Select}
                id="kaas-networkId"
                data-cy="kaasCreateForm-networkIdSelect"
                disabled={filteredNetworkOptions.length === 1}
                options={filteredNetworkOptions}
              />
            </FormControl>
          )}
          <FormControl
            label="Kubernetes version"
            tooltip="Kubernetes version running on the cluster"
            inputId="kaas-kubernetesVersion"
            errors={errors.kubernetesVersion}
          >
            <Field
              name="kubernetesVersion"
              as={Select}
              id="kaas-kubernetesVersion"
              data-cy="kaasCreateForm-kubernetesVersionSelect"
              options={cloudOptions.kubernetesVersions}
            />
          </FormControl>
        </>
      )}

      <MoreSettingsSection>
        <TextTip color="blue" className="mb-4">
          Metadata is only assigned to the environment in Portainer, i.e. the
          group and tags are not assigned to the cluster at the cloud provider
          end.
        </TextTip>
        <CustomTemplateSelector customTemplates={customTemplates} />
      </MoreSettingsSection>

      <ActionsSection
        onReloadClick={() => {
          setIsOptionsForce(true);
          cloudOptionsQuery.refetch();
        }}
        isReloading={
          cloudOptionsQuery.isLoading || cloudOptionsQuery.isFetching
        }
        isSubmitting={isSubmitting}
      />
    </>
  );
}
